import { Box, Spinner, Text } from '@chakra-ui/react';
import React, { memo, useMemo } from 'react';

const RenderedVideoCard = memo(function RenderedVideoCard({
  preview,
  ring,
  borderColor,
  onOpenPreview,
}) {
  const processingStates = useMemo(() => ['pending', 'in_queue', 'running'], []);
  const isProcessing = processingStates.includes(String(preview?.status));
  const isErrored = preview?.success === false || (preview?.error?.length ?? 0) > 0;

  const cardId = useMemo(
    () => String(preview?.media_job ?? preview?._id ?? Math.random()),
    [preview?.media_job, preview?._id]
  );

  const thumbSrc =
    preview?.videoThumbnail ||
    preview?.thumbnailUrl ||
    preview?.previewImage ||
    null;

  const clickable = !isErrored && !isProcessing;

  return (
    <Box key={cardId}>
      <Box
        role="group"
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={0}
        width="200px"
        height="300px"
        borderRadius="12px"
        border="1px solid"
        borderColor={borderColor}
        bg={isErrored ? 'gray.50' : 'transparent'}
        _hover={{
          cursor: clickable ? 'pointer' : 'default',
          border: isErrored ? '2px solid' : `2px solid ${ring}`,
          borderColor: isErrored ? 'gray.300' : ring,
          boxShadow: isErrored ? 'none' : 'md',
        }}
        onClick={() => {
          if (clickable) onOpenPreview(preview);
        }}
      >
        {isErrored ? (
          <Box
            w="full"
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            px={3}
            textAlign="center"
          >
            <Text fontSize="sm" color="gray.800">
              Failed to render this video.
            </Text>
          </Box>
        ) : isProcessing ? (
          <Box
            w="full"
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            px={3}
            textAlign="center"
            position="relative"
          >
            <Spinner mb={2} />
            <Text fontSize="md" fontWeight="bold" color="gray.600">
              Your video is still processing
            </Text>
            <Text fontSize="sm" color="gray.500">
              Please check back in a few minutes.
            </Text>
          </Box>
        ) : (
          <>
            {thumbSrc ? (
              <Box
                as="img"
                src={thumbSrc}
                alt="Video thumbnail"
                w="full"
                h="full"
                objectFit="cover"
                borderRadius="10px"
              />
            ) : (
              <Box
                as="video"
                src={preview?.videoUrl}
                muted
                controls={false}
                w="full"
                h="full"
                objectFit="cover"
                borderRadius="10px"
                preload="metadata"
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
});

export default RenderedVideoCard;
